import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';
import type { EChartsCoreOption } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { ThemeService } from '../../../core/services/theme.service';
import { baseText } from '../chart.types';

/** Overheating gauge 0–100 (the app's signature visual). */
@Component({
  selector: 'app-gauge-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
  templateUrl: './gauge-chart.component.html',
  styleUrl: './gauge-chart.component.scss',
})
export class GaugeChartComponent {
  private readonly theme = inject(ThemeService);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Null means the score could not be computed. It must NOT be drawn as a
   * needle at 50 — that is the "balanced" reading, and a viewer cannot tell a
   * balanced market from an unmeasured one.
   */
  score = input.required<number | null>();
  height = input<number>(190);

  options = computed<EChartsCoreOption>(() => {
    const dark = this.theme.theme() === 'dark';
    const t = baseText(dark);
    return {
      series: [
        {
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          radius: '100%',
          center: ['50%', '64%'],
          axisLine: {
            lineStyle: {
              width: 12,
              color: [
                [0.4, dark ? '#3FB8AE' : '#0F8B84'],
                [0.65, dark ? '#E2C86A' : '#C98A1B'],
                [1, dark ? '#E07A6B' : '#C64B33'],
              ],
            },
          },
          pointer: { length: '58%', width: 4, itemStyle: { color: t.ink } },
          axisTick: { show: false },
          splitLine: { length: 4, distance: 4, lineStyle: { color: t.muted, width: 1 } },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 34,
            fontWeight: 700,
            fontFamily: 'Sofia Sans Condensed',
            color: t.ink,
            offsetCenter: [0, '28%'],
          },
          data: [{ value: this.score() ?? 50 }],
        },
      ],
    };
  });
}
